/**
 * Shared deadline urgency tiers — consistent across all views.
 *
 * Tiers (from UX/UI recommendation #9):
 *   Critical  — < 7 days   → Red, pulsing
 *   Urgent    — 7–14 days  → Orange
 *   Warning   — 15–30 days → Yellow
 *   Healthy   — 30+ days   → Green
 */

export type UrgencyTier = 'closed' | 'critical' | 'urgent' | 'warning' | 'healthy';

export interface UrgencyStyle {
  tier: UrgencyTier;
  bg: string;
  text: string;
  border: string;
  /** True when daysLeft < 7 — apply `animate-deadline-pulse` class */
  pulse: boolean;
}

export function getDeadlineUrgency(daysLeft: number): UrgencyStyle {
  if (daysLeft <= 0) {
    return {
      tier: 'closed',
      bg: 'bg-ds-gray-4/30',
      text: 'text-ds-gray-3',
      border: 'border-ds-gray-4',
      pulse: false,
    };
  }
  if (daysLeft < 7) {
    return {
      tier: 'critical',
      bg: 'bg-ds-red/20',
      text: 'text-ds-red',
      border: 'border-ds-red/30',
      pulse: true,
    };
  }
  if (daysLeft <= 14) {
    return {
      tier: 'urgent',
      bg: 'bg-ds-gold/20',
      text: 'text-ds-gold',
      border: 'border-ds-gold/30',
      pulse: false,
    };
  }
  if (daysLeft <= 30) {
    return {
      tier: 'warning',
      bg: 'bg-ds-yellow/20',
      text: 'text-ds-yellow',
      border: 'border-ds-yellow/30',
      pulse: false,
    };
  }
  return {
    tier: 'healthy',
    bg: 'bg-ds-green/15',
    text: 'text-ds-green',
    border: 'border-ds-green/20',
    pulse: false,
  };
}
